import React from "react";

import AppearTransition from "@/components/elements/AppearTransition";
import WeldIconSmall from "@/components/icons/WeldIconSmall";
import ButtonPrimitive from "@/components/primitives/Button";
import classNames from "@/helpers/classNames";
import { XIcon } from "lucide-react";

import { subscribeToUpdates } from "./serviceWorkerRegistration";

export function AppUpdater() {
  const [onAccept, setOnAccept] = React.useState<(() => void) | null>(null);

  React.useEffect(() => {
    const unsubscribe = subscribeToUpdates((fn) => {
      // Wrap in a functional updater so React stores the function as the
      // state value instead of invoking it as an updater.
      setOnAccept(() => fn);
    });
    return () => {
      unsubscribe();
    };
  }, []);

  if (!onAccept) return null;
  return (
    <AppearTransition>
      <ToastPositioner>
        <AppUpdateToast
          onAccept={onAccept}
          onDismiss={() => setOnAccept(null)}
        />
      </ToastPositioner>
    </AppearTransition>
  );
}

export const ToastPositioner = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div">
>((props, ref) => {
  return (
    <div
      ref={ref}
      {...props}
      className={classNames(
        "fixed right-2 bottom-2 z-50 shadow-xs",
        props.className,
      )}
    />
  );
});

type AppUpdateToastProps = {
  onAccept: () => void;
  onDismiss: () => void;
};

export function AppUpdateToast(props: AppUpdateToastProps) {
  return (
    <div className="relative">
      <div
        className={classNames(
          "flex items-center gap-4 rounded-sm px-4 py-2",
          "text-white",
          "bg-gray-700 hover:bg-gray-700/90 active:bg-gray-700/75",
          "dark:bg-gray-600 dark:hover:bg-gray-600/75 dark:active:bg-gray-500",
          "group relative",
        )}
        role="button"
        onClick={() => props.onAccept()}
        tabIndex={0}
      >
        <div className="flex size-10 items-center justify-center rounded-full bg-white dark:bg-gray-800">
          <WeldIconSmall className="size-7 text-gray-800 transition-all duration-400 ease-in-out group-hover:rotate-90 group-focus:rotate-2880 group-focus:duration-1000 dark:text-gray-100" />
        </div>
        <div className="flex flex-col text-xs">
          <span className="text-sm font-medium">Update available</span>
          <span>Click here to update the Weld app!</span>
        </div>
      </div>
      <CloseButton
        onClick={() => {
          props.onDismiss();
        }}
      />
    </div>
  );
}

function CloseButton(props: React.ComponentProps<typeof ButtonPrimitive>) {
  return (
    <ButtonPrimitive
      className={classNames(
        "absolute top-1 right-1 rounded-sm p-0.5",
        "text-white",
        // "bg-black/0 hover:bg-black/20",
        "bg-white/0 hover:bg-white/10",
      )}
      {...props}
    >
      <XIcon className="size-5" />
    </ButtonPrimitive>
  );
}
